import axios, {
    type AxiosError,
    type AxiosInstance,
    type AxiosRequestConfig,
    type AxiosResponse,
    type InternalAxiosRequestConfig,
} from "axios";

const DEFAULT_TIMEOUT = 20_000;

/**
 * Расширенный конфиг HTTP-запроса с поддержкой отмены и `requestId`.
 */
export interface RequestConfig extends AxiosRequestConfig {
    /** ID запроса для программной отмены через {@link ApiClient.cancelRequest}. */
    requestId?: string;
}

type RequestInterceptor = (config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>;
type ResponseInterceptor = (response: AxiosResponse) => AxiosResponse;
type ErrorInterceptor = (error: AxiosError) => Promise<never>;

/**
 * Универсальный lazy HTTP-клиент: инициализация baseURL откладывается до выбора сервера.
 */
class ApiClient {
    private instance: AxiosInstance | null = null;
    private abortControllers = new Map<string, AbortController>();
    private requestInterceptors: RequestInterceptor[] = [];
    private responseInterceptors: ResponseInterceptor[] = [];
    private errorInterceptors: ErrorInterceptor[] = [];
    private pendingInstanceMutations: Array<(instance: AxiosInstance) => void> = [];
    private savedBaseConfig?: AxiosRequestConfig;

    constructor(baseConfig?: AxiosRequestConfig) {
        if (baseConfig != null) {
            this.initialize(baseConfig);
        }
    }

    /**
     * Инициализирует или переинициализирует axios-инстанс.
     */
    public initialize(baseConfig: AxiosRequestConfig): void {
        this.savedBaseConfig = baseConfig;
        this.instance = axios.create(baseConfig);
        this.applyInterceptorsToInstance(this.instance);

        this.pendingInstanceMutations.forEach((mut) => {
            if (this.instance != null) {
                mut(this.instance);
            }
        });
        this.pendingInstanceMutations = [];
    }

    /**
     * Устанавливает `baseURL` и переинициализирует клиент.
     */
    public setBaseUrl(baseURL: string): void {
        const config = { ...(this.savedBaseConfig ?? {}), baseURL };
        config.timeout ??= DEFAULT_TIMEOUT;
        this.initialize(config);
    }

    protected applyToInstance(mutator: (instance: AxiosInstance) => void): void {
        if (this.instance != null) {
            mutator(this.instance);
        } else {
            this.pendingInstanceMutations.push(mutator);
        }
    }

    private applyInterceptorsToInstance(instance: AxiosInstance): void {
        this.requestInterceptors.forEach((it) => {
            instance.interceptors.request.use((value) => it(value));
        });
        this.responseInterceptors.forEach((it) => {
            instance.interceptors.response.use((response) => it(response));
        });
        this.errorInterceptors.forEach((it) => {
            instance.interceptors.response.use(
                (response) => response,
                (error) => it(error),
            );
        });
    }

    /**
     * Регистрирует request interceptor и применяет его к текущему и будущим Axios instances.
     * @param interceptor обработчик конфигурации перед отправкой запроса
     */
    useRequestInterceptor(interceptor: RequestInterceptor): this {
        this.requestInterceptors.push(interceptor);
        if (this.instance != null) {
            this.instance.interceptors.request.use((value) => interceptor(value));
        }
        return this;
    }

    /**
     * Регистрирует response interceptor и применяет его к текущему и будущим Axios instances.
     * @param interceptor обработчик успешного HTTP-ответа
     */
    useResponseInterceptor(interceptor: ResponseInterceptor): this {
        this.responseInterceptors.push(interceptor);
        if (this.instance != null) {
            this.instance.interceptors.response.use((response) => interceptor(response));
        }
        return this;
    }

    /**
     * Регистрирует error interceptor и применяет его к текущему и будущим Axios instances.
     * @param interceptor обработчик transport/HTTP ошибок Axios
     */
    useErrorInterceptor(interceptor: ErrorInterceptor): this {
        this.errorInterceptors.push(interceptor);
        if (this.instance != null) {
            this.instance.interceptors.response.use(
                (response) => response,
                (error) => interceptor(error),
            );
        }
        return this;
    }

    private ensureInitialized(): void {
        if (this.instance == null) {
            throw new Error("ApiClient instance is not initialized. Call initialize() or setBaseUrl() first.");
        }
    }

    private resolveAbortSignal(config: RequestConfig): AbortSignal {
        const { requestId, signal: externalSignal } = config;

        if (requestId == null && externalSignal == null) {
            const controller = new AbortController();
            return controller.signal;
        }

        const controller = new AbortController();

        if (externalSignal != null) {
            if (externalSignal.aborted) {
                controller.abort();
            } else if (typeof externalSignal.addEventListener === "function") {
                externalSignal.addEventListener(
                    "abort",
                    () => {
                        controller.abort();
                    },
                    { once: true },
                );
            }
        }

        if (requestId != null) {
            this.abortControllers.set(requestId, controller);
        }

        return controller.signal;
    }

    /**
     * Выполняет HTTP-запрос и возвращает `response.data`.
     */
    public async request<T>(config: RequestConfig): Promise<T> {
        this.ensureInitialized();
        const instance = this.instance;
        if (instance == null) {
            throw new Error("ApiClient instance is not initialized.");
        }

        const requestId = config.requestId;
        const resolvedSignal = this.resolveAbortSignal(config);

        try {
            const response = await instance.request<T>({
                ...config,
                signal: resolvedSignal,
            });
            return response.data;
        } finally {
            if (requestId != null) {
                this.abortControllers.delete(requestId);
            }
        }
    }

    /**
     * Отменяет запрос по `requestId`.
     */
    public cancelRequest(requestId: string): void {
        const controller = this.abortControllers.get(requestId);
        if (controller != null) {
            controller.abort();
            this.abortControllers.delete(requestId);
        }
    }

    get<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: "GET", url });
    }

    post<T = unknown, D = unknown>(url: string, data?: D, config?: RequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: "POST", url, data });
    }

    put<T = unknown, D = unknown>(url: string, data?: D, config?: RequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: "PUT", url, data });
    }

    delete<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: "DELETE", url });
    }
}

export default ApiClient;
